import { ReactNode, useRef } from 'react'
import { useGSAP } from '@gsap/react'
import clsx from 'clsx'
import gsap from 'gsap'

import { useConfigurationStore, useFreeBuyBonusStore, useFreeSpinsBonusesStore } from '@stores'
import { ViewportType } from 'game-ui-types'

import { CloseButtonSVG } from './'

type BonusModalProps = {
    children: ReactNode
    isOpen: boolean
    width?: string | { portrait?: string; landscape?: string }
    showCloseIcon?: boolean
    closeOnOutsideClick?: boolean
    contentShadow?: boolean
    onClose?: () => void
}

export const BonusModal = ({
    isOpen,
    onClose,
    showCloseIcon = true,
    closeOnOutsideClick = true,
    contentShadow = true,
    width,
    children,
}: BonusModalProps) => {
    if (!isOpen) return null

    const viewport = useConfigurationStore((state) => state.viewport)
    const buyBonusConfig = useConfigurationStore((state) => state.buyBonusConfig)
    const isFBBModalOpened = useFreeBuyBonusStore((state) => state.isModalOpened)
    const isFSModalOpened = useFreeSpinsBonusesStore((state) => state.isModalOpened)

    const portraitViewport = viewport === ViewportType.MOBILE_PORTRAIT

    const modalWidth =
        typeof width === 'string'
            ? width
            : portraitViewport
              ? (width?.portrait ?? '746px')
              : (width?.landscape ?? '746px')

    const portraitStyles =
        portraitViewport && (isFBBModalOpened || isFSModalOpened)
            ? { alignItems: 'baseline', paddingTop: '275px' }
            : { alignItems: 'center' }
    const modalContentShadowClass = contentShadow ? 'shadow-[0_10px_10px_rgba(0,0,0,0.6)]' : ''
    const modalRef = useRef<HTMLDivElement>(null)

    useGSAP(() => {
        if (isOpen && modalRef.current) {
            gsap.fromTo(modalRef.current, { scale: 0 }, { scale: 1, duration: 0.6, ease: 'back.out(1.7)' })
        }
    }, [isOpen])

    if (!isOpen) return null

    return (
        <div id="modal-inset" style={portraitStyles} className="fixed inset-0 z-[102] flex justify-center">
            <div className="absolute inset-0 bg-black opacity-50" onClick={closeOnOutsideClick ? onClose : undefined} />
            <div
                id="bonus-modal"
                ref={modalRef}
                role="dialog"
                aria-modal="true"
                style={{ width: modalWidth }}
                className={clsx(
                    'relative z-10 flex flex-col rounded-[10px] text-white overflow-hidden',
                    modalContentShadowClass
                )}
            >
                {showCloseIcon && (
                    <button
                        id="close-modal"
                        onClick={onClose}
                        aria-label="Close modal"
                        className="absolute top-2 right-2 p-1"
                    >
                        <CloseButtonSVG color={buyBonusConfig.layout?.closeButtonColor} />
                    </button>
                )}
                {children}
            </div>
        </div>
    )
}
